import type { RideOffer } from "@tdelivr/api-client/generated/dispatch/ride-offer.js";
import { classOfStatus } from "@tdelivr/design-tokens";

import { absent, at, field, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the offer monitor does to the rows DSP-02 returns, away from any rendering.
 *
 * The same split as `demand-queue.ts` and `live-map.ts`, for the same reason: searching, ordering,
 * paging and — here — deciding what an offer's status actually is can all be stated and tested
 * without a browser, and a component that does them inline can only be checked by rendering it.
 *
 * One thing on this screen is not the usual "the design draws a field nobody publishes". It is the
 * opposite shape and it is worse: **DSP-02 publishes a `status` that is behind the clock, and
 * drawing it as written turns an offer nobody can answer any more into an offer a dispatcher is
 * still waiting on.** `lapsed()` and `effectiveStatus()` below are the whole of the fix, and R-10
 * in `docs/design/DESIGN_RECONCILIATION.md` is the measurement behind them.
 */

/** DSP-02's published status enum, as one name. */
export type OfferStatus = RideOffer["status"];

/**
 * The three statuses an offer can still move out of.
 *
 * DSP-02's own `liveOfferStatuses`, and its listing defaults to exactly these — so the board's
 * unfiltered page is the live set rather than every offer. Restated here rather than imported
 * because a console must not import a service's source; asserted against the published enum in
 * `tests/offer-monitor.test.ts` so it cannot drift silently.
 */
export const LIVE_STATUSES: readonly OfferStatus[] = ["created", "delivered", "viewed"];

/**
 * Every status the listing's `status` filter accepts, in the contract's own order.
 *
 * Seven, because the filter goes to the service rather than over the rows the browser holds. The
 * design builds its filter from the states present in its three sample rows, and over a real
 * listing that is a trap: the unfiltered page is live-only, so a filter built from it would offer
 * three options and an operator would conclude no offer was ever accepted. The four terminal
 * statuses are reachable only by asking DSP-02 for them by name.
 */
export const FILTERABLE_STATUSES: readonly OfferStatus[] = [
  "created",
  "delivered",
  "viewed",
  "accepted",
  "rejected",
  "expired",
  "cancelled",
];

/**
 * Whether this offer has run out of time, whatever its row says.
 *
 * **The finding this screen exists to record.** DSP-02 expires offers with a sweeper —
 * `POST /internal/v1/ride-offers/expire`, which takes an `at` and a `limit` and updates
 * `status IN ('created','delivered','viewed') AND expires_at <= $at` in batches. Between an
 * offer's `expiresAt` and the sweep that catches it, the row still reads `delivered`.
 *
 * It is not merely late. The domain refuses every transition on an offer past its `expiresAt`
 * except the expiry itself, with a 409 `offer_expired` — so an offer in that window is one the
 * driver cannot accept, cannot decline and cannot even be shown. It is finished, and its row says
 * it is open.
 *
 * A board that drew the stored status would put that offer under `delivered`, in the `waiting`
 * class, on the page whose whole purpose is "which offer is about to lapse unanswered". A
 * dispatcher would leave it alone and wait for an answer that the platform has already made
 * impossible. That is the absence that becomes a false statement rather than a blank cell, and the
 * clock it needs — `expiresAt` — is published on every row, so the screen can simply be right.
 *
 * Only the live three. A terminal offer's `expiresAt` is in the past on almost every row and means
 * nothing: an offer accepted in six seconds is accepted, not lapsed.
 */
export function lapsed(offer: RideOffer, now: number): boolean {
  if (!LIVE_STATUSES.includes(offer.status)) return false;
  const expiresAt = Date.parse(offer.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= now;
}

/**
 * The status to draw: the row's, unless the clock has overtaken it.
 *
 * `expired` rather than a tenth word of this screen's own, because that is the status the sweeper
 * is about to write and the one the domain already behaves as though it had written.
 */
export function effectiveStatus(offer: RideOffer, now: number): OfferStatus {
  return lapsed(offer, now) ? "expired" : offer.status;
}

/**
 * DSP-02's vocabulary in the design's words.
 *
 * Three of the seven are written on the design's own sample rows — `accepted`, `timed out`,
 * `declined` — so those are taken verbatim. The other four are drawn nowhere in the bundle, and the
 * plainest true word is used for each: the badge beside it carries the class, so the word only has
 * to name the fact.
 *
 * `created` and `delivered` are two different facts and the design has no row for either. An offer
 * that exists and has not reached a device is not an offer a driver is ignoring, and collapsing the
 * two would tell a dispatcher the driver is slow when the push never left.
 */
export function readableStatus(status: OfferStatus): string {
  switch (status) {
    case "created":
      return "not delivered";
    case "delivered":
      return "delivered";
    case "viewed":
      return "viewed";
    case "accepted":
      return "accepted";
    case "rejected":
      return "declined";
    case "expired":
      return "timed out";
    case "cancelled":
      return "cancelled";
  }
}

/**
 * Seconds between two published instants, rounded the way the design writes them.
 *
 * `undefined` when either end is missing or unparseable, so a caller has to decide what to say
 * rather than being handed a `NaN` to render.
 */
function elapsedSeconds(from: string, to: string | undefined): number | undefined {
  if (to === undefined) return undefined;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return undefined;
  return Math.max(0, Math.round((end - start) / 1000));
}

/**
 * The design's `Result` column, from the timestamps DSP-02 publishes.
 *
 * Its three samples are `accepted in 6 s`, `no answer in 15 s` and `declined · too far`, so the
 * grammar is "what happened, and how long it took". Every duration in it is reachable: DSP-02
 * stamps `deliveredAt`, `viewedAt`, `respondedAt`, `cancelledAt` and `expiredAt` beside the
 * `createdAt` every row carries.
 *
 * Measured from `createdAt` rather than from `deliveredAt`. Two reasons, and the second is the one
 * that decided it: `createdAt` is required on every row where `deliveredAt` is optional, so it is
 * the only end that yields a value for every offer; and the design's own `no answer in 15 s` is the
 * whole offer window, which is `expiresAt` less `createdAt`. Recorded as Q-14, because a driver who
 * received an offer four seconds late did not have fifteen seconds to answer it and the design does
 * not say which number it means.
 *
 * The one thing here with no source is `· too far`. DSP-02 records that an offer was rejected and
 * publishes nothing about why — there is no reason on the offer, no reason in the reject command,
 * and no operation anywhere that takes one. So the reason is absent rather than guessed.
 */
export function resultText(offer: RideOffer, now: number): string {
  if (lapsed(offer, now)) {
    // The sweeper has not caught it yet, so there is no `expiredAt` to measure to — but `expiresAt`
    // is when the window closed and the answer did not arrive, which is the same sentence.
    const window = elapsedSeconds(offer.createdAt, offer.expiresAt);
    return window === undefined ? "no answer" : `no answer in ${window} s`;
  }

  switch (offer.status) {
    case "created":
      // Not "waiting on the driver": nothing has reached one.
      return "not delivered yet";
    case "delivered":
    case "viewed": {
      const at = offer.status === "viewed" ? offer.viewedAt : offer.deliveredAt;
      const took = elapsedSeconds(offer.createdAt, at);
      const word = offer.status === "viewed" ? "viewed" : "delivered";
      return took === undefined ? `${word}, no time recorded` : `${word} in ${took} s`;
    }
    case "accepted":
    case "rejected": {
      const took = elapsedSeconds(offer.createdAt, offer.respondedAt);
      const word = offer.status === "accepted" ? "accepted" : "declined";
      return took === undefined ? word : `${word} in ${took} s`;
    }
    case "expired": {
      const took = elapsedSeconds(offer.createdAt, offer.expiredAt ?? offer.expiresAt);
      return took === undefined ? "no answer" : `no answer in ${took} s`;
    }
    case "cancelled": {
      const took = elapsedSeconds(offer.createdAt, offer.cancelledAt);
      return took === undefined ? "cancelled" : `cancelled in ${took} s`;
    }
  }
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type OfferOrder = "default" | "state" | "az" | "za";

export interface OfferQuery {
  /** Free text over every value on the row, as the design's grid searches. */
  readonly search: string;
  readonly order: OfferOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** Now, for the lapse rule, so searching `timed out` finds a row the sweeper has not reached. */
  readonly now: number;
  /** DRV-01's `displayName` by `driverId`, so a search matches the name an operator can see. */
  readonly names: ReadonlyMap<string, string>;
}

export interface OfferPage {
  /** The rows on the current page. */
  readonly rows: readonly RideOffer[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly RideOffer[];
  readonly total: number;
  readonly pageCount: number;
}

/**
 * Every word on a row that a search should match.
 *
 * The published values and the words on screen together, so an operator searching `expired`, one
 * searching `timed out` and one searching a driver's name all find the same row. The *effective*
 * status, not the stored one: a lapsed offer that still reads `delivered` must answer to
 * `timed out`, because that is what the board says about it.
 */
function searchableText(offer: RideOffer, query: OfferQuery): string {
  const status = effectiveStatus(offer, query.now);
  return [
    offer.offerId,
    offer.candidateRef.driverId,
    query.names.get(offer.candidateRef.driverId) ?? "",
    offer.demandRef.id,
    status,
    readableStatus(status),
    resultText(offer, query.now),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order DSP-02 answered in — `ORDER BY expires_at, offer_id`, soonest to expire
 * first — and the screen must not quietly replace it. `navigation.ts` says so in the page's own
 * entry and the service says so in its listing's summary: an offer monitor exists to show what is
 * about to lapse unanswered, and a board re-sorted by identifier buries exactly that row.
 *
 * There is no state filter here. The design filters its three sample rows in the browser; over a
 * real listing that cannot work, because the unfiltered page is DSP-02's live set and the four
 * terminal statuses are not in it at any page. So the filter is a query parameter, handled by the
 * component's fetch, and this function sees only what came back.
 */
export function offerPage(rows: readonly RideOffer[], query: OfferQuery): OfferPage {
  const needle = query.search.trim().toLowerCase();
  const matched =
    needle.length === 0
      ? [...rows]
      : rows.filter((offer) => searchableText(offer, query).includes(needle));

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return readableStatus(effectiveStatus(left, query.now)).localeCompare(
              readableStatus(effectiveStatus(right, query.now)),
            );
          }
          // "Name" is the first column, as the design's A–Z is: the offer, not the driver.
          const compared = left.offerId.localeCompare(right.offerId);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence this board owes an operator about what it actually fetched, or nothing.
 *
 * The design gives the grid no such line, and the demand queue added one for the same reason R-08
 * defends: an operator working a partial board while believing it whole is the failure it exists
 * to prevent. Here it carries the more important of the two facts, because DSP-02's unfiltered
 * listing is its live three — so "All states" means every offer still open, and an operator who
 * reads it as every offer concludes that nothing was accepted all morning.
 *
 * Truncation wins when both are true, because a partial page is the stronger claim: 200 of 400
 * offers read as all of them is worse than a default nobody has noticed the shape of.
 *
 * @param fetched how many rows came back, or `undefined` before anything has.
 * @param truncated whether DSP-02 answered a cursor, meaning this is the head of the list.
 * @param status the state the operator chose, or `undefined` for the service's own default.
 */
export function boundedNote(
  fetched: number | undefined,
  truncated: boolean,
  status: OfferStatus | undefined,
  perFetch: number,
): string | undefined {
  if (fetched === undefined) return undefined;
  if (truncated) {
    return `More than ${perFetch} offers match. This board shows the first ${perFetch}, so the count is a floor rather than a total.`;
  }
  if (status === undefined) {
    return "All states is every offer still open. DSP-02 answers accepted, declined, timed out and cancelled offers only when one is chosen above.";
  }
  return undefined;
}

/**
 * The design's `Driver` cell: `M. Achieng · drv_4410`.
 *
 * Both halves have a source and the join is the one the live map already makes — DSP-02's
 * `candidateRef.driverId` into DRV-01's `driverId`, which R-09 established for the roster. It is
 * the identifier DSP-01 ranked the candidate under, and DSP-01 takes its supply from DRV-05 and
 * DRV-06, which key on the same `driverId` the whole driver domain now uses.
 *
 * The name half is a second read under a second pair of scopes, and `navigation.ts` grants this
 * page neither. So it is optional by construction: an operator without `driver-profile:read` sees
 * the identifier alone rather than an error, which is what the design's cell degrades to anyway.
 */
export function driverCell(offer: RideOffer, names: ReadonlyMap<string, string>): string {
  const id = offer.candidateRef.driverId;
  const name = names.get(id);
  return name === undefined ? id : `${name} · ${id}`;
}

/**
 * The selected offer, for the panel beside the monitor.
 *
 * **The headline is the effective status, not the published one**, and that is this board's whole
 * character. DSP-02 has no worker expiring its own rows, so an offer whose `expiresAt` has passed
 * is still `created` or `delivered` on the wire; `effectiveStatus` reads the clock the way the
 * grid does. The published value is carried beside it as its own field, because an operator
 * reporting a row needs the value the service will answer with, not the one the screen computed.
 *
 * **The five timestamps are the reason this panel exists.** `createdAt`, `deliveredAt`, `viewedAt`,
 * `respondedAt`, `cancelledAt` and `expiredAt` are a delivery trail — minted, pushed, seen,
 * answered — and the grid shows one column of relative time. Each is dropped when the offer has not
 * reached that step, so the lines present are the steps that happened.
 *
 * **`rank` and `generation` are DSP-01's, and they are why this offer went to this driver.** A
 * dispatcher asking "why them" is asking for these two, and nothing else on the board carries them.
 */
export function offerDetail(
  offer: RideOffer,
  names: ReadonlyMap<string, string>,
  now: number,
): RecordDetail {
  const effective = effectiveStatus(offer, now);
  const classKey = classOfStatus("DSP-02", effective);
  const name = names.get(offer.candidateRef.driverId);

  return {
    headline: readableStatus(effective),
    identifier: offer.offerId,
    source: name === undefined ? "DSP-02" : "DSP-02 · DRV-01",
    ...(classKey === undefined
      ? {}
      : {
          state: {
            classKey,
            label: readableStatus(effective),
            code: "DSP-02",
            status: effective,
          },
        }),
    fields: [
      ...(effective === offer.status
        ? []
        : [
            field("Published status", offer.status),
            /*
             * Said in full on the one row where the screen and the service disagree. The grid
             * carries this as a tooltip; a panel is where somebody writes it into a message.
             */
          ]),
      ...optional("Driver", name),
      field("Driver reference", offer.candidateRef.driverId, true),
      ...optional("Driver account", offer.driverUserId, true),
      // DSP-01's answer to "why this driver", which nothing else on this board carries.
      field("Candidate rank", String(offer.candidateRef.rank)),
      field("Candidate generation", String(offer.candidateRef.generation)),
      field("Demand", offer.demandRef.id, true),
      field("Demand version", String(offer.demandRef.version)),
      field("Expires", at(offer.expiresAt) ?? offer.expiresAt),
      field("Created", at(offer.createdAt) ?? offer.createdAt),
      ...optional("Delivered", at(offer.deliveredAt)),
      ...optional("Viewed", at(offer.viewedAt)),
      ...optional("Responded", at(offer.respondedAt)),
      ...optional("Cancelled", at(offer.cancelledAt)),
      ...optional("Expired", at(offer.expiredAt)),
      absent(
        "Why it was declined",
        "DSP-02 records that an offer was rejected and not what the driver said. There is no reason field on the contract.",
      ),
      field("Version", `v${String(offer.version)}`),
    ],
    ...(lapsed(offer, now)
      ? {
          note: "Past its expiry and still live on the wire. DSP-02 runs no expiry worker, so this row ages on the screen's clock rather than the service's — the whole reason this board reads the clock itself.",
        }
      : {}),
  };
}
